import {
  type Entities,
  type EntitiesSource,
  type Entity,
  EntityType,
  type GenericMap,
  type LocalSettings,
  type ModuleEntity
} from "@client/types";
import { type ComponentAppState } from "@client/types/AppState/component";
import { type PopupAppState } from "@client/types/AppState/popups";
import { omit } from "lodash";
import { isModuleEntity } from "./layout";
import { EMPTY_OBJECT } from "./misc";
import { deepClone } from "./object";

export const collectChildrenIds = (entities: Entities, id: string) =>
  (entities[id].childrenIds || []).reduce((res, cid) => [...res, cid, ...collectChildrenIds(entities, cid)], []);

export const getParentEntity = (entities: Entities, id: string, match: (e: Entity) => boolean): Entity | null => {
  let entity = entities[id];
  while (entity.parentId && !match(entity)) {
    entity = entities[entity.parentId];
  }
  return entity.parentId ? entity : null;
};

export const removeEntityChildren = (entities: Entities, id: string): Entities => {
  const entity = entities[id];
  if (!entity || !entity.childrenIds) return entities;

  const clone = deepClone(entities);
  clone[id].childrenIds = [];
  return omit(clone, collectChildrenIds(entities, id));
};

export const getEntityDataAttributes = (id: string, viewonly: boolean = false): GenericMap<string> => {
  const result = {
    "data-eid": id
  };

  if (viewonly) {
    result["data-ad"] = true;
  }

  return result;
};

type GetSourceEntitiesArgs = {
  activePopup: PopupAppState | undefined;
  content: Entities;
  components: ComponentAppState[];
};
export const getSourceEntities = (source: EntitiesSource, args: GetSourceEntitiesArgs): Entities => {
  switch (source) {
    case "content":
      return args.content;
    case "popup":
      if (!args.activePopup) {
        alert("BUG in getSourceEntities: popup");
        throw new Error("BUG");
      }
      return args.activePopup.entities;
    default: {
      if (!source) {
        alert("BUG in getSourceEntities");
        throw new Error("BUG");
      }

      switch (source.type) {
        case "component": {
          const gc = args.components.find((x) => x.id === source.id)!;
          return gc.entities;
        }
        case "layout":
          return EMPTY_OBJECT;
      }
    }
  }
};

type GetSourceLocalSettingsArgs = {
  activePopup: PopupAppState | undefined;
  content: LocalSettings;
  components: ComponentAppState[];
};
export const getSourceLocalSettings = (source: EntitiesSource, args: GetSourceLocalSettingsArgs): LocalSettings => {
  switch (source) {
    case "content":
      return args.content;
    case "popup":
      if (!args.activePopup) {
        alert("BUG in getSourceLocalSettings");
        throw new Error("BUG");
      }
      return args.activePopup.local;
    default: {
      if (!source) {
        alert("BUG in getSourceLocalSettings");
        throw new Error("BUG");
      }

      switch (source.type) {
        case "component": {
          const gc = args.components.find((x) => x.id === source.id)!;
          return gc.local;
        }
        case "layout":
          return EMPTY_OBJECT;
      }
    }
  }
};

export const getModuleType = (entities: Entities, entity: Entity): string | null =>
  isModuleEntity(entity)
    ? entity.moduleType
    : entity.type === EntityType.ModuleItem
    ? (entities[entity.parentId!] as ModuleEntity).moduleType
    : null;

export const fixPropNames = (data: GenericMap<any>): Entities => {
  return Object.keys(data).reduce((res, x) => {
    const { Id, Settings, settings, id, ...e } = data[x];

    res[x] = {
      ...e,
      id: Id ?? id,
      settings: Settings ?? settings
    };

    return res;
  }, {});
};
